import { Fragment, useMemo } from "react";
import { Container } from "@/components/container";
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from "@/partials/toolbar";
import { useLayout } from "@/providers";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { useGetSubscriptionTrialConfigQuery } from "@/services/SubscriptionTrialConfigService";
import type { ApiResponse } from "@/types/api.types";
import { SubscriptionTrialForm } from "@/pages/configuration/components/SubscriptionTrialForm";

const SubscriptionTrialConfiguration = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { hasPermission } = usePermissions();
  const canRead = hasPermission("subscription_management_read");
  const canUpdate = hasPermission("subscription_management_update");

  const modeParam = searchParams.get("mode");
  const mode = (
    modeParam === "edit" || modeParam === "create" || modeParam === "view"
      ? modeParam
      : "view"
  ) as "view" | "edit" | "create";

  const { data: policy, isLoading } = useGetSubscriptionTrialConfigQuery(
    undefined,
    {
      skip: !canRead,
    },
  );

  const normalizedPolicy = useMemo(() => {
    if (!policy) return null;
    const api = policy as ApiResponse<{
      subscription_id?: string;
      trial_subscription_id?: string;
      trial_days?: number;
    }>;
    if (api?.data) {
      return {
        subscription_id: String(
          api.data.subscription_id ?? api.data.trial_subscription_id ?? ""
        ),
        trial_days:
          typeof api.data.trial_days === "number" ? api.data.trial_days : 0,
      };
    }
    return null;
  }, [policy]);

  const globalMode = useMemo(() => {
    if (!normalizedPolicy) return "create";
    return mode;
  }, [mode, normalizedPolicy]);

  const readOnlyReason = useMemo(() => {
    if (isLoading) {
      return "Loading subscription trial configuration...";
    }

    if (!canRead) {
      return "You don't have permission to view subscription trial configuration.";
    }

    if (globalMode !== "view" && !canUpdate) {
      return "You don't have permission to update subscription trial configuration.";
    }

    return undefined;
  }, [canRead, canUpdate, globalMode, isLoading]);

  const handleDone = () => {
    navigate("/configuration/subscription-trial");
  };

  const handleEdit = () => {
    navigate("/configuration/subscription-trial?mode=edit");
  };

  return (
    <Fragment>
      {currentLayout?.name === "demo1-layout" && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="subscription-trial-configuration-heading" />
              <ToolbarDescription>
                Configure which subscription plan is eligible for trial and how
                many days the trial lasts.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              {normalizedPolicy && canUpdate && globalMode === "view" && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleEdit}
                >
                  Edit
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <Container>
        <SubscriptionTrialForm
          mode={globalMode}
          onDone={handleDone}
          readOnlyReason={readOnlyReason}
        />
      </Container>
    </Fragment>
  );
};

export { SubscriptionTrialConfiguration };
